import { Navigation } from "@/components/navigation";
import { HeroSection } from "@/components/hero-section";
import { DriverBenefits } from "@/components/driver-benefits";
import { LazySection } from "@/components/lazy-section";
import { Footer } from "@/components/footer";
import { useEffect } from "react";
import { updateCanonicalUrl, CANONICAL_DOMAIN } from "@/lib/seo-canonical";

export default function Home() {
  useEffect(() => {
    // Set canonical URL for homepage
    updateCanonicalUrl("/");
    
    // Add structured data for SEO - LocalBusiness with explicit Texas ServiceArea
    const structuredData = {
      "@context": "https://schema.org",
      "@type": ["LocalBusiness", "MovingCompany"],
      "@id": `${CANONICAL_DOMAIN}/#localbusiness`,
      "name": "Ben Boaz Transport LLC",
      "description": "Reliable regional transportation services using 48-foot and 53-foot flatbed trailers for steel, lumber, coils, building materials, and machinery.",
      "url": CANONICAL_DOMAIN,
      "telephone": "(832) 732-5501",
      "priceRange": "$$",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Houston",
        "addressRegion": "TX",
        "addressCountry": "US"
      },
      "areaServed": [
        {
          "@type": "State",
          "name": "Texas",
          "sameAs": "https://en.wikipedia.org/wiki/Texas"
        },
        {
          "@type": "City",
          "name": "Houston",
          "sameAs": "https://en.wikipedia.org/wiki/Houston"
        },
        {
          "@type": "City",
          "name": "Dallas",
          "sameAs": "https://en.wikipedia.org/wiki/Dallas"
        },
        {
          "@type": "City",
          "name": "San Antonio",
          "sameAs": "https://en.wikipedia.org/wiki/San_Antonio"
        },
        {
          "@type": "City",
          "name": "Austin",
          "sameAs": "https://en.wikipedia.org/wiki/Austin,_Texas"
        },
        {
          "@type": "City",
          "name": "Fort Worth",
          "sameAs": "https://en.wikipedia.org/wiki/Fort_Worth,_Texas"
        }
      ],
      "serviceArea": {
        "@type": "GeoShape",
        "name": "Texas and Surrounding Regions",
        "addressCountry": "US",
        "box": "25.8371 -106.6456 36.5007 -93.5083"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Flatbed Trucking Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Steel & Metal Hauling",
              "description": "Flatbed transport of steel pipes, I-beams, coils, and metal fabrications"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Lumber & Building Materials Transport",
              "description": "Safe transport of dimensional lumber, plywood, and construction materials"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Heavy Machinery Transport",
              "description": "Secure transportation of construction equipment and industrial machinery"
            }
          }
        ]
      },
      "serviceType": "Flatbed Trucking Services"
    };

    const jobPostingData = {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      "title": "Professional CDL Truck Driver",
      "description": "Join our team of professional drivers at Ben Boaz Transport LLC. We offer competitive pay, old school ELD-Exempt equipment (1999 & older Peterbilts and Freightliners), 48' and 53' flatbed trailers, home weekly schedules, and dedicated routes. Haul steel, lumber, and machinery across Texas and surrounding regions. CDL-A required with clean driving record.",
      "identifier": {
        "@type": "PropertyValue",
        "name": "Ben Boaz Transport LLC",
        "value": "CDL-DRIVER-TX-001"
      },
      "datePosted": new Date().toISOString().split('T')[0],
      "hiringOrganization": {
        "@type": "Organization",
        "name": "Ben Boaz Transport LLC",
        "sameAs": CANONICAL_DOMAIN,
        "logo": `${CANONICAL_DOMAIN}/og-image.png`
      },
      "jobLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Houston Metropolitan Area",
          "addressLocality": "Houston",
          "addressRegion": "TX",
          "postalCode": "77001",
          "addressCountry": "US"
        }
      },
      "baseSalary": {
        "@type": "MonetaryAmount",
        "currency": "USD",
        "value": {
          "@type": "QuantitativeValue",
          "minValue": 55000,
          "maxValue": 85000,
          "unitText": "YEAR"
        }
      },
      "employmentType": "FULL_TIME",
      "workHours": "Regional routes with home weekly",
      "jobBenefits": "Dedicated routes, competitive pay, home weekly, ELD-exempt equipment, family atmosphere",
      "qualifications": "Valid CDL-A license, clean driving record, flatbed experience preferred",
      "responsibilities": "Haul steel, lumber, machinery, and building materials using 48' and 53' flatbed trailers across Texas and surrounding states",
      "industry": "Transportation and Trucking",
      "occupationalCategory": "53-3032.00 Heavy and Tractor-Trailer Truck Drivers",
      "validThrough": new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString()
    };

    const faqData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What types of freight does Ben Boaz Transport haul?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We specialize in flatbed hauling of steel, lumber, coils, building materials, and machinery using 48-foot and 53-foot trailers across Texas and surrounding regions."
          }
        },
        {
          "@type": "Question",
          "name": "What are the requirements to become a driver at Ben Boaz Transport?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Valid CDL-A license, clean driving record, professional experience with flatbed trucking, and ability to operate 48' and 53' flatbed trailers. We offer competitive pay and home weekly schedules."
          }
        },
        {
          "@type": "Question",
          "name": "What regions does Ben Boaz Transport serve?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We provide regional flatbed trucking services throughout Texas and surrounding states, specializing in reliable transportation with modern equipment."
          }
        }
      ]
    };

    // Organization schema for Knowledge Panel eligibility
    const organizationData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Ben Boaz Transport LLC",
      "alternateName": "Ben Boaz Transport",
      "url": CANONICAL_DOMAIN,
      "logo": {
        "@type": "ImageObject",
        "url": `${CANONICAL_DOMAIN}/og-image.png`,
        "width": 1456,
        "height": 816
      },
      "image": `${CANONICAL_DOMAIN}/og-image.png`,
      "description": "Texas-based regional flatbed trucking company specializing in steel, lumber, and machinery hauling with ELD-exempt old school equipment.",
      "telephone": "(832) 732-5501",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Houston",
        "addressRegion": "TX",
        "addressCountry": "US"
      },
      "areaServed": {
        "@type": "State",
        "name": "Texas"
      },
      "foundingDate": "2020",
      "numberOfEmployees": {
        "@type": "QuantitativeValue",
        "minValue": 10,
        "maxValue": 50
      },
      "sameAs": [
        "https://www.facebook.com/BenBoazTransport"
      ],
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "(832) 732-5501",
          "contactType": "customer service",
          "areaServed": "US",
          "availableLanguage": ["English", "Spanish"]
        },
        {
          "@type": "ContactPoint",
          "telephone": "(832) 732-5501",
          "contactType": "sales",
          "areaServed": "US",
          "availableLanguage": ["English", "Spanish"],
          "description": "Freight quotes and shipping inquiries"
        }
      ],
      "knowsAbout": [
        "Flatbed trucking",
        "Steel hauling",
        "Lumber transportation",
        "Machinery transport",
        "Regional trucking Texas",
        "CDL driver jobs"
      ]
    };

    // BreadcrumbList schema for navigation context
    const breadcrumbData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": CANONICAL_DOMAIN
        }
      ]
    };

    // WebSite schema for sitelinks search box eligibility
    const websiteData = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Ben Boaz Transport LLC",
      "alternateName": "Ben Boaz Transport",
      "url": CANONICAL_DOMAIN,
      "description": "Professional flatbed trucking services and CDL driver jobs in Texas",
      "publisher": {
        "@type": "Organization",
        "name": "Ben Boaz Transport LLC",
        "logo": {
          "@type": "ImageObject",
          "url": `${CANONICAL_DOMAIN}/og-image.png`
        }
      },
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${CANONICAL_DOMAIN}/blog?search={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    };

    const script1 = document.createElement("script");
    script1.id = "home-business-structured-data";
    script1.type = "application/ld+json";
    script1.text = JSON.stringify(structuredData);
    document.head.appendChild(script1);

    const script2 = document.createElement("script");
    script2.id = "home-job-structured-data";
    script2.type = "application/ld+json";
    script2.text = JSON.stringify(jobPostingData);
    document.head.appendChild(script2);

    const script3 = document.createElement("script");
    script3.id = "home-faq-structured-data";
    script3.type = "application/ld+json";
    script3.text = JSON.stringify(faqData);
    document.head.appendChild(script3);

    const script4 = document.createElement("script");
    script4.id = "home-organization-structured-data";
    script4.type = "application/ld+json";
    script4.text = JSON.stringify(organizationData);
    document.head.appendChild(script4);

    const script5 = document.createElement("script");
    script5.id = "home-breadcrumb-structured-data";
    script5.type = "application/ld+json";
    script5.text = JSON.stringify(breadcrumbData);
    document.head.appendChild(script5);

    const script6 = document.createElement("script");
    script6.id = "home-website-structured-data";
    script6.type = "application/ld+json";
    script6.text = JSON.stringify(websiteData);
    document.head.appendChild(script6);

    return () => {
      const s1 = document.getElementById("home-business-structured-data");
      const s2 = document.getElementById("home-job-structured-data");
      const s3 = document.getElementById("home-faq-structured-data");
      const s4 = document.getElementById("home-organization-structured-data");
      const s5 = document.getElementById("home-breadcrumb-structured-data");
      const s6 = document.getElementById("home-website-structured-data");
      if (s1) document.head.removeChild(s1);
      if (s2) document.head.removeChild(s2);
      if (s3) document.head.removeChild(s3);
      if (s4) document.head.removeChild(s4);
      if (s5) document.head.removeChild(s5);
      if (s6) document.head.removeChild(s6);
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Navigation />
      <main id="main-content">
        <HeroSection />
        <DriverBenefits />
        <LazySection loader={() => import("@/components/contact-section").then(m => m.ContactSection)} />
        <LazySection loader={() => import("@/components/services-section").then(m => m.ServicesSection)} />
        <LazySection loader={() => import("@/components/fleet-gallery").then(m => m.FleetGallery)} />
        <LazySection loader={() => import("@/components/testimonials-section").then(m => m.TestimonialsSection)} />
        <LazySection loader={() => import("@/components/company-story").then(m => m.CompanyStory)} />
      </main>
      <Footer />
    </div>
  );
}
